// Snowplow Analytics Browser Tracker Setup
// see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/quick-start-guide/
// and https://github.com/Upheal/upheal-analytics/blob/main/book/tracking/web-app.md
import { newTracker, setUserId } from '@snowplow/browser-tracker';
import {
    LinkClickTrackingPlugin,
    enableLinkClickTracking,
} from '@snowplow/browser-plugin-link-click-tracking';
import {
    ErrorTrackingPlugin,
    enableErrorTracking,
} from '@snowplow/browser-plugin-error-tracking';
import { TimezonePlugin } from '@snowplow/browser-plugin-timezone';
import { User } from '../../types';
import { LOGGER_ENABLED } from '@upheal/utils/logger';

export const COLLECTOR_URL =
    import.meta.env.VITE_REACT_APP_SERVER_STAGE === 'prod'
        ? 'https://sp-collector-prod.api.upheal.io'
        : 'https://sp-collector.api.upheal.io';

export const initSnowplowTracker = () => {
    if (!LOGGER_ENABLED) {
        return;
    }

    // Initialization options - https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/tracker-setup/initialization-options/
    newTracker(
        import.meta.env.VITE_REACT_APP_SERVER_STAGE === 'prod' ? 'prod' : 'dev',
        COLLECTOR_URL,
        {
            appId: 'app.upheal.io',
            discoverRootDomain: true,
            respectDoNotTrack: true,
            cookieSameSite: 'Lax',
            stateStorageStrategy: 'cookieAndLocalStorage',
            plugins: [
                LinkClickTrackingPlugin(),
                ErrorTrackingPlugin(),
                TimezonePlugin(),
            ],
            contexts: {
                webPage: true,
                session: true,
            },
            // custom path to mitigate impact of adblockers - see https://docs.snowplow.io/docs/get-started/tracking/cookies-and-ad-blockers/#2-mitigating-the-impact-of-ad-blockers).
            postPath: '/track',
        },
    );

    // For details about `refreshLinkTracking` and implicit `trackLinkClick`
    // see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/tracking-events/#link-click-tracking
    enableLinkClickTracking();

    // For details how to track checked (use `trackError`) and unchecked errors (automatic with `enableErrorTracking()`)
    // see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/plugins/error-tracking/
    enableErrorTracking();
};

export const setSnowplowUser = (user: User) => {
    if (!LOGGER_ENABLED) {
        return;
    }

    // Set user id by using one of following methods:
    // see https://docs.snowplow.io/docs/collecting-data/collecting-from-own-applications/javascript-trackers/browser-tracker/browser-tracker-v3-reference/tracker-setup/additional-options/
    setUserId(user.id);
};
